import React, { useEffect } from 'react';
import { Shield, Lock, Eye, FileText, ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../data';

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-20 space-y-16">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-brand-light via-white to-white py-16 sm:py-20 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center space-y-6">
          <span className="text-xs font-bold tracking-widest text-brand-blue uppercase bg-brand-light px-3 py-1 rounded-full inline-flex items-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            Sikkerhed & Tryghed
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-deep leading-tight tracking-tight">
            Privatlivspolitik
          </h1>
          <p className="text-base sm:text-lg text-brand-dark/80 leading-relaxed max-w-2xl mx-auto">
            Hos <Link to="/" className="text-brand-deep hover:text-brand-blue font-semibold underline decoration-brand-blue/30 underline-offset-2 transition-colors">{COMPANY_INFO.name}</Link> prioriterer vi beskyttelsen af dine personlige data højt. Her kan du læse, hvordan vi behandler dine oplysninger i overensstemmelse med GDPR.
          </p>
          <div className="pt-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-blue hover:text-brand-deep transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tilbage til forsiden
            </Link>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="bg-white border border-brand-light rounded-2xl p-6 sm:p-10 shadow-sm space-y-10">
          
          {/* Quick Summary Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pb-8 border-b border-brand-light">
            <div className="flex gap-3 items-start">
              <div className="p-2.5 bg-brand-light text-brand-blue rounded-lg shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-brand-deep text-sm">100% Fortroligt</h4>
                <p className="text-xs text-brand-dark/70 mt-0.5">Vi sælger eller videregiver aldrig dine data til tredjeparter med henblik på markedsføring.</p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <div className="p-2.5 bg-brand-light text-brand-blue rounded-lg shrink-0">
                <Eye className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-brand-deep text-sm">Fuld Transparens</h4>
                <p className="text-xs text-brand-dark/70 mt-0.5">Du har altid ret til at få indsigt i, hvilke oplysninger vi har registreret om dig.</p>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <div className="p-2.5 bg-brand-light text-brand-blue rounded-lg shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-brand-deep text-sm">Nem Sletning</h4>
                <p className="text-xs text-brand-dark/70 mt-0.5">Du kan til enhver tid bede om at få slettet dine personoplysninger fra vores systemer.</p>
              </div>
            </div>
          </div>

          {/* Policy Text */}
          <div className="prose prose-brand max-w-none text-brand-dark/90 leading-relaxed space-y-8">
            
            {/* Section 1 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">1</span>
                Dataansvarlig
              </h2>
              <p className="text-sm sm:text-base">
                <Link to="/" className="text-brand-deep hover:text-brand-blue font-semibold underline decoration-brand-blue/30 underline-offset-2 transition-colors">{COMPANY_INFO.name}</Link> har base i Smørum og udfører professionelt glarmesterarbejde for kunder i Egedal, Nordsjælland og store dele af Sjælland. Vi er dataansvarlig for behandlingen af de personoplysninger, som vi modtager om dig. Vi sikrer, at dine personoplysninger behandles i overensstemmelse med lovgivningen.
              </p>
              <div className="bg-brand-light/50 border border-brand-light p-4 rounded-xl space-y-2 text-sm text-brand-dark/80 mt-2">
                <p className="font-semibold text-brand-deep">{COMPANY_INFO.name}</p>
                <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-blue shrink-0" /> {COMPANY_INFO.address} (Base i Smørum – dækker Egedal, Nordsjælland og store dele af Sjælland)</p>
                <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand-blue shrink-0" /> Tlf: {COMPANY_INFO.phone}</p>
                <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-brand-blue shrink-0" /> E-mail: {COMPANY_INFO.email}</p>
                <p className="font-medium">CVR-nr: {COMPANY_INFO.cvr}</p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">2</span>
                Formål med behandlingen
              </h2>
              <p className="text-sm sm:text-base">
                Vi indsamler og behandler dine personoplysninger til specifikke og legitime formål, når du interagerer med os. Formålene omfatter:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base">
                <li>Behandling og besvarelse af dine henvendelser via vores kontaktformularer.</li>
                <li>Planlægning og udførelse af gratis opmålingsbesøg og rådgivning på din adresse.</li>
                <li>Udarbejdelse og fremsendelse af uforpligtende tilbud på energiruder, termoruder eller andre glasløsninger.</li>
                <li>Gennemførelse og levering af bestilte glarmesteropgaver samt efterfølgende fakturering.</li>
                <li>Overholdelse af gældende lovgivning, herunder bogføringsloven.</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">3</span>
                Kategorier af personoplysninger
              </h2>
              <p className="text-sm sm:text-base">
                Vi behandler primært almindelige personoplysninger, som du selv giver os i forbindelse med en henvendelse eller bestilling. Dette inkluderer:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base">
                <li><strong>Identifikationsoplysninger:</strong> Navn, efternavn og eventuelt firmanavn.</li>
                <li><strong>Kontaktoplysninger:</strong> Telefonnummer, e-mailadresse og fysisk adresse (hvor opmålingen eller monteringen skal finde sted).</li>
                <li><strong>Brugsdata:</strong> IP-adresse, browsertype og adfærd på vores hjemmeside (opsamlet anonymt via cookies til optimering af brugeroplevelsen).</li>
              </ul>
              <p className="text-sm sm:text-base text-brand-dark/70 italic">
                Vi indsamler eller behandler aldrig følsomme personoplysninger (f.eks. helbredsoplysninger, race eller religion).
              </p>
            </div>

            {/* Section 4 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">4</span>
                Retsgrundlag for behandlingen
              </h2>
              <p className="text-sm sm:text-base">
                Behandlingen af dine personoplysninger sker på baggrund af følgende retsgrundlag:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base">
                <li><strong>Kontraktuelle forpligtelser (GDPR art. 6, stk. 1, litra b):</strong> Behandlingen er nødvendig for at kunne opfylde en aftale med dig, f.eks. at levere et tilbud eller udføre en glarmesteropgave.</li>
                <li><strong>Samtykke (GDPR art. 6, stk. 1, litra a):</strong> Når du indsender en kontaktformular eller booker et gratis besøg, giver du samtykke til, at vi må kontakte dig angående din forespørgsel.</li>
                <li><strong>Retlig forpligtelse (GDPR art. 6, stk. 1, litra c):</strong> Vi er forpligtet til at gemme visse fakturaoplysninger i henhold til bogføringsloven.</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">5</span>
                Modtagere af personoplysninger
              </h2>
              <p className="text-sm sm:text-base">
                Vi behandler dine personoplysninger med stor fortrolighed. Vi videregiver ikke dine oplysninger til tredjeparter, medmindre det er nødvendigt for at levere vores service eller overholde lovgivningen. Vi anvender betroede databehandlere (f.eks. hostingudbydere, IT-leverandører og bogføringssystemer), som udelukkende behandler data på vores vegne og efter vores instrukser i henhold til en skriftlig databehandleraftale.
              </p>
            </div>

            {/* Section 6 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">6</span>
                Opbevaring og sletning
              </h2>
              <p className="text-sm sm:text-base">
                Vi opbevarer kun dine personoplysninger, så længe det er nødvendigt til de formål, de er indsamlet til:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base">
                <li>Henvendelser via kontaktformularer, der ikke fører til et tilbud eller samarbejde, slettes senest 6 måneder efter endt korrespondance.</li>
                <li>Oplysninger relateret til tilbud og aftaler opbevares i op til 5 år efter opgavens afslutning af hensyn til garanti (Glarmestergaranti) og reklamationsret.</li>
                <li>Fakturaer og regnskabsmateriale opbevares i 5 år fra udgangen af det pågældende regnskabsår i overensstemmelse med bogføringsloven.</li>
              </ul>
            </div>

            {/* Section 7 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">7</span>
                Dine rettigheder
              </h2>
              <p className="text-sm sm:text-base">
                Du har efter databeskyttelsesforordningen en række rettigheder i forhold til vores behandling af oplysninger om dig:
              </p>
              <ul className="list-disc pl-5 space-y-1.5 text-sm sm:text-base">
                <li><strong>Ret til indsigt:</strong> Du har ret til at få indsigt i de oplysninger, som vi behandler om dig.</li>
                <li><strong>Ret til berigtigelse:</strong> Du har ret til at få urigtige oplysninger om dig selv rettet.</li>
                <li><strong>Ret til sletning ("retten til at blive glemt"):</strong> I særlige tilfælde har du ret til at få slettet oplysninger om dig, inden tidspunktet for vores almindelige generelle sletning indtræffer.</li>
                <li><strong>Ret til begrænsning af behandling:</strong> Du har i visse tilfælde ret til at få behandlingen af dine personoplysninger begrænset.</li>
                <li><strong>Ret til indsigelse:</strong> Du har i visse tilfælde ret til at gøre indsigelse mod vores ellers lovlige behandling af dine personoplysninger.</li>
                <li><strong>Ret til dataportabilitet:</strong> Du har i visse tilfælde ret til at modtage dine personoplysninger i et struktureret, almindeligt anvendt og maskinlæsbart format.</li>
              </ul>
              <p className="text-sm sm:text-base">
                Hvis du ønsker at gøre brug af dine rettigheder, bedes du kontakte os på <a href={`mailto:${COMPANY_INFO.email}`} className="text-brand-blue hover:underline font-semibold">{COMPANY_INFO.email}</a>.
              </p>
            </div>

            {/* Section 8 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">8</span>
                Klagemulighed
              </h2>
              <p className="text-sm sm:text-base">
                Du har ret til at indgive en klage til Datatilsynet, hvis du er utilfreds med den måde, vi behandler dine personoplysninger på. Du finder Datatilsynets kontaktoplysninger på deres hjemmeside: <a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline font-semibold">www.datatilsynet.dk</a>.
              </p>
            </div>

            {/* Section 9 */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-brand-deep flex items-center gap-2">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-brand-light text-brand-blue text-sm font-bold">9</span>
                Opdateringer
              </h2>
              <p className="text-sm sm:text-base">
                Vi forbeholder os retten til at opdatere og ændre denne privatlivspolitik. Den seneste version vil altid være tilgængelig på vores hjemmeside.
              </p>
              <p className="text-xs text-brand-dark/50 pt-4 border-t border-brand-light">
                Senest opdateret: 16. juni 2026
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
